import type { BacktestReport, SidePerformance, Trade, TradeLeg } from "@trader/backtester"
import type { Instrument, Timeframe } from "@trader/shared/MarketData"
import { type PointerEvent as ReactPointerEvent, useState } from "react"
import { GearIcon } from "../../ui/icons.tsx"
import { editorDraftFor, strategyEditor } from "../strategy-editor/editorPanel.ts"
import { removeStrategy, setStrategyRange, setStrategyStart, type StrategyInstance, updateStrategy } from "../workspace/workspaceStore.ts"
import { reportCurrencyText } from "./accountCurrency.ts"
import { EquityChart } from "./EquityChart.tsx"
import { formatMoney, formatNumber, formatPercent, formatTime, tone } from "./format.ts"
import { PROPERTY_FIELDS } from "./properties.ts"
import { BacktestVsPaper } from "../trading/BacktestVsPaper.tsx"
import { usePaneRunnerReport } from "../trading/useRunnerReport.ts"
import { AttachRunnerDialog } from "./AttachRunnerDialog.tsx"
import { StrategySettingsDialog } from "./StrategySettingsDialog.tsx"
import { legTime } from "./StrategyLayer.ts"
import { strategyInfoFor, strategyResults, usePaneBacktest } from "./strategyResults.ts"
import { testerPanel, useTesterCollapsed } from "./testerPanel.ts"

type Tab = "overview" | "performance" | "trades" | "properties" | "paper"

const TABS: ReadonlyArray<readonly [Tab, string]> = [
  ["overview", "Overview"],
  ["performance", "Performance Summary"],
  ["trades", "List of Trades"],
  ["properties", "Properties"]
]

/** How report times are shown (in Indian time): with the clock for intraday bars. */
interface Venue {
  readonly intraday: boolean
}

function Metric({ label, sub, subTone = "", value, valueTone = "" }: {
  readonly label: string
  readonly value: string
  readonly valueTone?: string
  readonly sub?: string
  readonly subTone?: string
}) {
  return (
    <div className="metric">
      <span className="metric-label">{label}</span>
      <span className={`metric-value ${valueTone}`}>{value}</span>
      {sub && <span className={`metric-sub ${subTone}`}>{sub}</span>}
    </div>
  )
}

function Overview({ barTimes, report, venue }: {
  readonly report: BacktestReport
  readonly barTimes: ReadonlyArray<number>
  readonly venue: Venue
}) {
  const p = report.performance
  const all = p.all
  return (
    <div className="tester-overview">
      <div className="metrics">
        <Metric
          label="Net profit"
          value={formatMoney(all.netProfit, report.currency)}
          valueTone={tone(all.netProfit)}
          sub={formatPercent(all.netProfitPercent)}
          subTone={tone(all.netProfit)}
        />
        <Metric label="Total closed trades" value={formatNumber(all.totalTrades)} />
        <Metric label="Percent profitable" value={formatPercent(all.percentProfitable)} />
        <Metric label="Profit factor" value={formatNumber(all.profitFactor)} />
        <Metric
          label="Max drawdown"
          value={formatMoney(p.maxStrategyDrawDown, report.currency)}
          valueTone="down"
          sub={formatPercent(p.maxStrategyDrawDownPercent)}
        />
        <Metric
          label="Avg trade"
          value={formatMoney(all.avgTrade, report.currency)}
          valueTone={tone(all.avgTrade)}
          sub={formatPercent(all.avgTradePercent)}
        />
        <Metric label="Avg # bars in trades" value={formatNumber(all.avgBarsInTrade)} />
      </div>
      <EquityChart
        equity={report.equity}
        buyHold={report.buyHold}
        initialCapital={report.properties.initialCapital}
        barTimes={barTimes}
        intraday={venue.intraday}
      />
    </div>
  )
}

/** "loss": an amount the report gives as positive, shown red as in TradingView. */
type Kind = "money" | "loss" | "percent" | "number"

/** TradingView's Performance Summary rows, each with All / Long / Short columns. */
const SUMMARY_ROWS: ReadonlyArray<readonly [string, (side: SidePerformance) => number | null, Kind]> = [
  ["Net profit", (s) => s.netProfit, "money"],
  ["Gross profit", (s) => s.grossProfit, "money"],
  ["Gross loss", (s) => s.grossLoss, "loss"],
  ["Commission paid", (s) => s.commissionPaid, "loss"],
  ["Profit factor", (s) => s.profitFactor, "number"],
  ["Max contracts held", (s) => s.maxContractsHeld, "number"],
  ["Total closed trades", (s) => s.totalTrades, "number"],
  ["Total open trades", (s) => s.totalOpenTrades, "number"],
  ["Number winning trades", (s) => s.numberOfWiningTrades, "number"],
  ["Number losing trades", (s) => s.numberOfLosingTrades, "number"],
  ["Percent profitable", (s) => s.percentProfitable, "percent"],
  ["Avg trade", (s) => s.avgTrade, "money"],
  ["Avg winning trade", (s) => s.avgWinTrade, "money"],
  ["Avg losing trade", (s) => s.avgLosTrade, "loss"],
  ["Ratio avg win / avg loss", (s) => s.ratioAvgWinAvgLoss, "number"],
  ["Largest winning trade", (s) => s.largestWinTrade, "money"],
  ["Largest losing trade", (s) => s.largestLosTrade, "loss"],
  ["Avg # bars in trades", (s) => s.avgBarsInTrade, "number"],
  ["Avg # bars in winning trades", (s) => s.avgBarsInWinTrade, "number"],
  ["Avg # bars in losing trades", (s) => s.avgBarsInLossTrade, "number"]
]

function PerformanceSummary({ report }: { readonly report: BacktestReport }) {
  const p = report.performance
  const money = (value: number | null) => formatMoney(value, report.currency)
  const format = (value: number | null, kind: Kind) =>
    kind === "money" || kind === "loss" ? money(value) : kind === "percent" ? formatPercent(value) : formatNumber(value)
  const toneOf = (value: number | null, kind: Kind) =>
    kind === "money" ? tone(value) : kind === "loss" && value !== null && value !== 0 ? "down" : ""
  const whole = (label: string, value: number, percent: number | null, className = tone(value)) => (
    <tr>
      <td>{label}</td>
      <td colSpan={3} className={className}>{money(value)} <small>{formatPercent(percent)}</small></td>
    </tr>
  )
  return (
    <table className="tester-table summary-table">
      <thead>
        <tr>
          <th />
          <th>All</th>
          <th>Long</th>
          <th>Short</th>
        </tr>
      </thead>
      <tbody>
        {whole("Max run-up", p.maxStrategyRunUp, p.maxStrategyRunUpPercent, "")}
        {whole("Max drawdown", p.maxStrategyDrawDown, p.maxStrategyDrawDownPercent, "down")}
        {whole("Buy & hold return", p.buyHoldReturn, p.buyHoldReturnPercent)}
        {whole("Open P&L", p.openPL, p.openPLPercent)}
        {SUMMARY_ROWS.map(([label, read, kind]) => (
          <tr key={label}>
            <td>{label}</td>
            {[p.all, p.long, p.short].map((side, i) => {
              const value = read(side)
              return <td key={i} className={toneOf(value, kind)}>{format(value, kind)}</td>
            })}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

/** One leg's cells: type, signal, time, price. */
function LegCells({ label, leg, venue }: { readonly label: string; readonly leg: TradeLeg; readonly venue: Venue }) {
  return (
    <>
      <td className="text">{label}</td>
      <td className="text">{leg.signal}</td>
      <td>{formatTime(legTime(leg.time), venue.intraday)}</td>
      <td>{formatNumber(leg.price)}</td>
    </>
  )
}

/** TradingView's List of Trades, newest first: each trade is an exit row over its entry row. */
function ListOfTrades({ pane, report, venue }: { readonly report: BacktestReport; readonly pane: number; readonly venue: Venue }) {
  if (report.trades.length === 0) return <p className="muted tester-empty">No trades in this range.</p>
  const money = (value: number) => formatMoney(value, report.currency)
  const side = (trade: Trade) => (trade.direction === "long" ? "long" : "short")
  return (
    <table className="tester-table trades-table">
      <thead>
        <tr>
          <th>#</th>
          <th className="text">Type</th>
          <th className="text">Signal</th>
          <th>Date / time</th>
          <th>Price</th>
          <th>Contracts</th>
          <th>Profit</th>
          <th>Cum. profit</th>
          <th>Run-up</th>
          <th>Drawdown</th>
        </tr>
      </thead>
      {report.trades.map((trade, i) => ({ trade, number: i + 1 })).reverse().map(({ number, trade }) => (
        <tbody key={number} className="trade">
          <tr onClick={() => strategyResults.focus(pane, legTime(trade.exit.time))}>
            <td rowSpan={2}>{number}</td>
            <LegCells label={trade.open ? "Open" : `Exit ${side(trade)}`} leg={trade.exit} venue={venue} />
            <td rowSpan={2}>{formatNumber(trade.qty)}</td>
            <td rowSpan={2} className={tone(trade.profit)}>
              {money(trade.profit)}
              <small>{formatPercent(trade.profitPercent)}</small>
            </td>
            <td rowSpan={2} className={tone(trade.cumProfit)}>{money(trade.cumProfit)}</td>
            <td rowSpan={2}>
              {money(trade.runUp)}
              <small>{formatPercent(trade.runUpPercent)}</small>
            </td>
            <td rowSpan={2}>
              {money(trade.drawdown)}
              <small>{formatPercent(trade.drawdownPercent)}</small>
            </td>
          </tr>
          <tr onClick={() => strategyResults.focus(pane, legTime(trade.entry.time))}>
            <LegCells label={`Entry ${side(trade)}`} leg={trade.entry} venue={venue} />
          </tr>
        </tbody>
      ))}
    </table>
  )
}

/** "percent_of_equity" → "percent of equity", true → "yes". */
const propertyText = (value: unknown): string =>
  typeof value === "boolean" ? (value ? "yes" : "no") : typeof value === "string" ? value.replaceAll("_", " ") : String(value)

function Properties({ onEdit, report, venue }: {
  readonly report: BacktestReport
  readonly venue: Venue
  readonly onEdit: () => void
}) {
  const properties = report.properties as unknown as Record<string, unknown>
  return (
    <div className="tester-properties">
      <table className="tester-table">
        <tbody>
          {PROPERTY_FIELDS.map((field) => (
            <tr key={field.id}>
              <td>{field.title}</td>
              <td>{propertyText(properties[field.id])}</td>
            </tr>
          ))}
          <tr>
            <td>Currency</td>
            <td>{reportCurrencyText(report)}</td>
          </tr>
          <tr>
            <td>Testing period</td>
            <td>
              {formatTime(legTime(report.range.from), venue.intraday)} –{" "}
              {formatTime(legTime(report.range.to), venue.intraday)}
            </td>
          </tr>
        </tbody>
      </table>
      <button type="button" onClick={onEdit}>Edit properties</button>
    </div>
  )
}

/** The strategy's properties with the account currency back to the symbol's. */
const withoutCurrency = (properties: Readonly<Record<string, unknown>>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(properties).filter(([key]) => key !== "currency"))

// The panel's height is this browser's preference (whether it's collapsed: testerPanel.ts).
const HEIGHT_KEY = "trader.tester.height"
const MIN_HEIGHT = 160
/** Of the window; CSS caps the panel at 75% of the workspace too. */
const MAX_HEIGHT_RATIO = 0.75

const loadHeight = (fallback: number): number => {
  try {
    const value = Number(localStorage.getItem(HEIGHT_KEY))
    return Number.isFinite(value) && value >= MIN_HEIGHT ? value : fallback
  } catch {
    return fallback
  }
}

const saveHeight = (height: number) => {
  try {
    localStorage.setItem(HEIGHT_KEY, String(height))
  } catch {
    // Not remembered; the panel still works.
  }
}

/**
 * TradingView's Strategy Tester, under the charts, for the active pane's
 * strategy: Overview, Performance Summary, List of Trades, Properties.
 *
 * The panel stays open (unless collapsed) while any visible chart has a
 * strategy, so clicking between charts doesn't resize them all. Without one
 * anywhere it's just its header, with a way to add a strategy.
 */
export function StrategyTester({ anyStrategy, chartLabel, instrument, onAddStrategy, pane, strategy, timeframe }: {
  readonly pane: number
  readonly strategy: StrategyInstance | null | undefined
  readonly instrument: Instrument | null
  readonly timeframe: Timeframe
  /** Symbol and timeframe of the active chart, e.g. "NSE:TCS · 15m". */
  readonly chartLabel: string
  /** Some chart in the layout has a strategy. */
  readonly anyStrategy: boolean
  /** Opens the strategy list for the active chart. */
  readonly onAddStrategy: () => void
}) {
  const [tab, setTab] = useState<Tab>("overview")
  const collapsed = useTesterCollapsed()
  const [height, setHeight] = useState(() => loadHeight(320))
  const [settings, setSettings] = useState<"inputs" | "properties" | null>(null)
  const [paperTrade, setPaperTrade] = useState(false)
  const backtest = usePaneBacktest(pane)
  // The chart's strategy paper trading on the followed account (docs/design/0007).
  const paper = usePaneRunnerReport(strategy && instrument ? instrument.id : null)
  const tabs = paper === null ? TABS : [...TABS, ["paper", "vs Paper"] as const]
  const shownTab: Tab = tab === "paper" && paper === null ? "overview" : tab
  const problem = backtest?.currencyProblem ?? null
  const startAt = problem?.startAt ?? null

  const result = backtest?.result ?? null
  const report = result?.report
  const title = strategy ? strategyInfoFor(backtest, strategy.id)?.title : undefined
  const venue: Venue | null = instrument && { intraday: timeframe !== "1D" }
  // A user strategy or a draft opens in the editor.
  const editDraft = editorDraftFor(strategy?.id)
  const expanded = !collapsed && anyStrategy

  // Dragging the top edge resizes the panel (the charts take the rest).
  const startResize = (event: ReactPointerEvent<HTMLDivElement>) => {
    const startY = event.clientY
    const maxHeight = window.innerHeight * MAX_HEIGHT_RATIO
    // A height saved on a taller window starts the drag from what's shown.
    const startHeight = Math.min(height, maxHeight)
    let latest = startHeight
    const onMove = (move: PointerEvent) => {
      latest = Math.round(Math.min(maxHeight, Math.max(MIN_HEIGHT, startHeight + startY - move.clientY)))
      setHeight(latest)
    }
    const onUp = () => {
      window.removeEventListener("pointermove", onMove)
      window.removeEventListener("pointerup", onUp)
      saveHeight(latest)
    }
    window.addEventListener("pointermove", onMove)
    window.addEventListener("pointerup", onUp)
  }

  return (
    <section className="strategy-tester" style={expanded ? { height } : undefined}>
      {expanded && <div className="tester-resize" onPointerDown={startResize} title="Drag to resize" />}
      <header className="tester-header">
        <button
          type="button"
          className="tester-title"
          // Nothing to show yet: the title leads to the strategies instead of doing nothing.
          onClick={() => (anyStrategy ? testerPanel.setCollapsed(!collapsed) : onAddStrategy())}
          aria-expanded={expanded}
        >
          <span className="tester-chevron">{expanded ? "▾" : "▴"}</span>
          Strategy Tester
        </button>
        {!strategy && (
          <>
            <button type="button" className="primary tester-add" onClick={onAddStrategy}>Add strategy</button>
            <span className="muted tester-chart">to backtest {chartLabel}</span>
            <button type="button" className="link" title="Write a strategy in the editor" onClick={strategyEditor.create}>
              New strategy
            </button>
          </>
        )}
        {strategy && (
          <>
            <span className="tester-strategy" title={`On ${chartLabel}`}>{title ?? "…"}</span>
            <span className="muted tester-chart">{chartLabel}</span>
            <button type="button" className="icon-button" title="Settings" onClick={() => setSettings("inputs")}>
              <GearIcon />
            </button>
            <nav className="tester-tabs" role="tablist">
              {tabs.map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-label={id === "paper" ? "Backtest vs paper" : undefined}
                  title={id === "paper" ? "Backtest vs paper: the strategy's backtest trades next to the paper account's fills" : undefined}
                  aria-selected={expanded && shownTab === id}
                  onClick={() => {
                    setTab(id)
                    testerPanel.open()
                  }}
                >
                  {label}
                </button>
              ))}
            </nav>
          </>
        )}
        <span className="toolbar-spacer" />
        {strategy && (
          <>
            {backtest?.running
              ? <span className="muted">Running…</span>
              : report && (
                <span className="muted" title="Bars backtested and time taken">
                  {formatNumber(backtest?.barTimes.length)} bars · {formatNumber(report.elapsedMs)} ms
                </span>
              )}
            {strategy.from !== undefined && venue && (
              <span className="tester-from" title="The backtest starts at this bar instead of the first one (Properties › Backtesting range)">
                From {formatTime(strategy.from, venue.intraday)}
                <button type="button" className="link" title="Start at the first bar again" onClick={() => setStrategyRange(pane, null, strategy.to ?? null)}>
                  ✕
                </button>
              </span>
            )}
            {strategy.to !== undefined && (
              <span className="tester-from" title="The backtest ends with this day (Properties › Backtesting range)">
                To {formatTime(strategy.to - 1, false)}
                <button type="button" className="link" title="Run to the latest bar again" onClick={() => setStrategyRange(pane, strategy.from ?? null, null)}>
                  ✕
                </button>
              </span>
            )}
            <label
              className="tester-deep"
              title="Run on the stored history of this symbol and timeframe, not just the bars loaded in the chart"
            >
              <input type="checkbox" checked={strategy.deep} onChange={(event) => updateStrategy(pane, { deep: event.target.checked })} />
              Deep backtest
            </label>
            {editDraft !== null
              ? (
                <button type="button" className="link" title="Open this strategy in the editor" onClick={() => strategyEditor.edit(editDraft)}>
                  Edit
                </button>
              )
              : (
                <button type="button" className="link" title="Write a strategy in the editor" onClick={strategyEditor.create}>
                  New strategy
                </button>
              )}
            {instrument && (
              <button type="button" className="link" title="Trade this strategy on a paper account" onClick={() => setPaperTrade(true)}>
                Paper trade…
              </button>
            )}
            <button type="button" className="link" onClick={onAddStrategy}>Change</button>
            <button type="button" className="link" onClick={() => removeStrategy(pane)}>Remove</button>
          </>
        )}
      </header>
      {expanded && (
        // Keyed by tab: each tab opens scrolled to its top.
        <div className="tester-body" key={tab}>
          {!strategy && (
            <div className="tester-empty">
              <p className="muted">No strategy on {chartLabel}. The tester shows the active chart's strategy.</p>
              <button type="button" className="primary" onClick={onAddStrategy}>Add strategy</button>
            </div>
          )}
          {strategy && backtest?.error && <p className="down tester-empty">The strategy stopped: {backtest.error}</p>}
          {strategy && problem && (
            <div className="tester-empty">
              <p className="down">Can't backtest in {String(strategy.properties["currency"] ?? "")}: {problem.message}</p>
              <p className="tester-actions">
                {startAt !== null && venue && (
                  <button type="button" className="primary" onClick={() => setStrategyStart(pane, startAt)}>
                    Start at {formatTime(startAt, venue.intraday)}
                  </button>
                )}
                {instrument && (
                  <button type="button" onClick={() => updateStrategy(pane, { properties: withoutCurrency(strategy.properties) })}>
                    Backtest in {instrument.currency}
                  </button>
                )}
              </p>
            </div>
          )}
          {strategy && !backtest?.error && !problem && result === null && (
            <p className="muted tester-empty">
              {backtest?.running ? "Running the backtest…" : "Waiting for this chart's bars to backtest."}
            </p>
          )}
          {strategy && result && !report && !backtest?.error && !problem && (
            <p className="muted tester-empty">The strategy placed no orders on these bars.</p>
          )}
          {strategy && report && venue && !backtest?.error && !problem && (
            <>
              {shownTab === "overview" && <Overview report={report} barTimes={backtest?.barTimes ?? []} venue={venue} />}
              {shownTab === "performance" && <PerformanceSummary report={report} />}
              {shownTab === "trades" && <ListOfTrades report={report} pane={pane} venue={venue} />}
              {shownTab === "properties" && <Properties report={report} venue={venue} onEdit={() => setSettings("properties")} />}
            </>
          )}
          {shownTab === "paper" && paper !== null && instrument && (
            <div className="tester-paper">
              <p className="muted">
                {paper.runner.strategyName} on {paper.account.name}: its backtest run forward on the account's bars, next to
                the account's paper fills.
              </p>
              {paper.error && <p className="down">Couldn't load the comparison: {paper.error}</p>}
              {paper.report === null
                ? !paper.error && <p className="muted">Loading…</p>
                : (
                  <BacktestVsPaper
                    report={paper.report}
                    currency={paper.account.currency}
                    pricePrecision={instrument.pricePrecision}
                  />
                )}
            </div>
          )}
        </div>
      )}
      {strategy && paperTrade && instrument && (
        <AttachRunnerDialog
          strategy={strategy}
          title={title ?? strategy.id}
          defaultProperties={{ ...strategyInfoFor(backtest, strategy.id)?.defaultProperties }}
          instrument={instrument}
          timeframe={timeframe}
          onClose={() => setPaperTrade(false)}
        />
      )}
      {strategy && settings && instrument && (
        <StrategySettingsDialog
          pane={pane}
          instance={strategy}
          initialTab={settings}
          symbolCurrency={instrument.currency}
          onClose={() => setSettings(null)}
        />
      )}
    </section>
  )
}
